import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import ToggleSwitch from '../../../../../shared/components/ToggleSwitch';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { StepperRouting } from './shared/StepperRouting';
import { ICampaignLegal } from '../../../../../model/briefing/step8/ICampaignLegal';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { BriefingService } from '../service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { UploadImage } from '../../../../../shared/components/UploadImage';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import { RiCloseCircleFill } from 'react-icons/ri';
import { clearHtmlFromString, hasAccessRights, oneStepUp } from '../../../../../shared/functions/Functions';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import { scrollToTop } from '../../../../../utils/helpers/ScrollToTop';
import DOMPurify from 'dompurify';
import { FiInfo } from 'react-icons/fi';
import { PaymentTermsEnum } from '../../../../../enum/PaymenTermsEnum';
import { selectedCampaignSlice } from '../../../../../store/brand/activeCampaignSlice';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { CampaignValidationStatusEnum } from '../../../../../enum/CampaignValidationStatusEnum';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import TextEditor from '../../../../../shared/components/TextEditor';

export const StepEight = () => {
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isSubmitClicked, setIsSubmitClicked] = useState(false);
    const [triggerUpdate, setTriggerUpdate] = useState(false);
    const [isNextClicked, setIsNextClicked] = useState(false);
    const {
        OfferItemDurationEnum,
        InvoicePeriodEnum,
        PaymentTermsEnum: PaymentTermsEnumArr,
    } = useSelector((states: IAllStates) => states.enums);
    const [files, setFiles] = useState<Blob[] | MediaSource[]>();
    const [exclusivityCompetitors, setExclusivityCompetitors] = useState([
        {
            id: 0,
            description: '',
        },
    ]);
    const [currentStep, setCurrentStep] = useState<ICampaignLegal>();
    const isCampaignApproved = [CampaignValidationStatusEnum.APPROVED].includes(
        (currentStep?.approvalStatus as CampaignValidationStatusEnum) || ''
    );
    const isPaymentDisabledByAcl = !hasAccessRights(AccessRightEnum.CAN_EDIT_PAYMENT_TERMS);
    const isDisabled = isCampaignApproved;
    const isPaymentDisabled = isCampaignApproved || isPaymentDisabledByAcl;

    useEffect(() => {
        !!id &&
            BriefingService.getBriefingByStep(id ?? '', 'eighth')
                .then(response => {
                    setCurrentStep(response);
                    setExclusivityCompetitors(response?.exclusivityCompetitors);
                    dispatch(selectedCampaignSlice.actions.setActiveCampaignId(String(response?.id)));
                })
                .catch(error => ErrorToast(error));
    }, [id, triggerUpdate]);

    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        setCurrentStep((prev: any) => ({ ...prev, [paramKey]: paramValue }));
    };

    const changeSwitchHandler = (isChecked: boolean, name: string) => {
        setCurrentStep((prev: any) => ({ ...prev, [name]: isChecked }));
    };

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        DOMPurify().sanitize(value);
        setCurrentStep((prev: any) => ({ ...prev, [name]: DOMPurify().sanitize(value) }));
    };

    const changeExclusivityHandler = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number
    ) => {
        const { name, value } = event.target;
        const copy = [...exclusivityCompetitors];
        const currentIndex = copy?.findIndex((item, ind) => ind === index);
        copy[currentIndex] = {
            ...copy[currentIndex],
            [name]: value,
        };
        setExclusivityCompetitors(copy);
    };

    const deleteSelectedImage = (imageId: string) => {
        !!imageId &&
            BriefingService.deleteBriefingImageByStep('eighth/bylaw', imageId)
                .then(response => {
                    if (response === '') {
                        SuccessToast(`${t('general.alert.success_deleted_image')}`);
                        setTriggerUpdate(prev => !prev);
                    }
                })
                .catch(error => ErrorToast(error));
    };

    const addCompetitors = () => {
        if (exclusivityCompetitors?.length < 3) {
            const copy = [...exclusivityCompetitors];
            copy.push({
                id: 0,
                description: '',
            });
            setExclusivityCompetitors(copy);
        } else {
            WarningToast(t('brand.campaign.briefing.step8.max_competitors_added'));
        }
    };

    const removeCompetitors = (index: number) => {
        const copy = [...exclusivityCompetitors];
        const filtered = copy?.filter((item, ind) => ind !== index);
        setExclusivityCompetitors(filtered);
    };

    const submitHandler = () => {
        setIsSubmitClicked(true);
        const hasExclusivityErrors =
            currentStep?.hasExclusivity && (!currentStep?.exclusivityDescription || !currentStep?.exclusivityDuration);
        const hasBylawsErrors =
            currentStep?.hasByLaws && !clearHtmlFromString(currentStep?.byLawsExplanation ?? '')?.length;
        if (!currentStep?.bylaws?.length ? hasExclusivityErrors || hasBylawsErrors : false) {
            return scrollToTop();
        }

        const isOtherPaymentTermsWithoutCustom =
            !isPaymentDisabled &&
            currentStep?.paymentTerms === PaymentTermsEnum.OTHER &&
            !currentStep.paymentTermsCustom;
        const isPaymentTermsMissing = !currentStep?.paymentTerms && !isPaymentDisabled;

        if (isOtherPaymentTermsWithoutCustom || isPaymentTermsMissing) {
            return scrollToTop();
        }
        setIsNextClicked(true);
        const formData = new FormData();
        if (!!files?.length) {
            for (const item of files) {
                formData.append('files', item as string | Blob);
            }
        }
        formData.append(
            'body',
            JSON.stringify({
                ...currentStep,
                exclusivityCompetitors,
            })
        );
        if (id) {
            BriefingService.updateBriefingStepEight(id, formData)
                .then(response => {
                    if (response) {
                        oneStepUp(navigate, id ?? '', response.isBriefingComplete, response.briefingStep);
                        SuccessToast(`${t('general.alert.success_updated_campaign_step')}`);
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => {
                    setIsNextClicked(false);
                });
        }
    };

    return (
        <Row className="mb-5 mb-md-0">
            <Col md={8}>
                {!currentStep?.isBriefingComplete && (
                    <div className="mb-4">
                        <ProgressBarCampaign title={t('brand.campaign.briefing.step8.legal')} percent={75} />
                    </div>
                )}
                <CardLayout>
                    <div className="mb-3 row">
                        <div className="col-md-6 mb-2 mb-md-0">
                            <div className="mb-2">
                                <h6 className="font-weight-bold mb-0 ">
                                    {t('brand.campaign.briefing.step8.payment_terms_brand')}
                                </h6>
                                <span className="text-muted">
                                    {t('brand.campaign.briefing.step8.payment_description')}
                                </span>
                            </div>
                            <div className="d-flex align-items-center">
                                <CustomAutocomplete
                                    dataArray={PaymentTermsEnumArr}
                                    isMultiple={false}
                                    defaultArray={currentStep?.paymentTerms ? [currentStep?.paymentTerms] : ''}
                                    isWithImage={false}
                                    classes="w-md-70"
                                    inputLabel="Payment terms"
                                    hasError={isSubmitClicked && !currentStep?.paymentTerms}
                                    isDisabled={isPaymentDisabled}
                                    disableClearable={true}
                                    queryFilterKey="paymentTerms"
                                    inputLabelTranslation={t('brand.campaign.briefing.step8.payment_terms_brand')}
                                    handleChangeParams={multipleSelectHandler}
                                />
                                {isPaymentDisabled && (
                                    <TooltipOverlay
                                        className="ms-1"
                                        content={t(
                                            `brand.campaign.briefing.step8.${
                                                isPaymentDisabledByAcl
                                                    ? 'payment_info_tooltip_acl'
                                                    : 'payment_info_tooltip_status'
                                            }`
                                        )}
                                    >
                                        <FiInfo size={14} />
                                    </TooltipOverlay>
                                )}
                            </div>
                        </div>
                        {currentStep?.isLongTerm && (
                            <div className="col-md-6 d-flex flex-column justify-content-between">
                                <div className="d-flex flex-column mb-2">
                                    <h6 className="font-weight-bold mb-0 ">
                                        {t('brand.campaign.briefing.step8.payout_method')}
                                    </h6>
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step8.payout_method_desc')}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <CustomAutocomplete
                                        dataArray={InvoicePeriodEnum}
                                        isMultiple={false}
                                        defaultArray={currentStep?.invoicePeriod ? [currentStep?.invoicePeriod] : ''}
                                        isWithImage={false}
                                        classes="w-md-70"
                                        inputLabel="Payment method"
                                        isDisabled={true}
                                        disableClearable={true}
                                        queryFilterKey="invoicePeriod"
                                        inputLabelTranslation={t('brand.campaign.briefing.step8.payout_method')}
                                        handleChangeParams={multipleSelectHandler}
                                    />
                                </div>
                            </div>
                        )}

                        {currentStep?.paymentTerms === PaymentTermsEnum.OTHER && (
                            <div>
                                <Form.Control
                                    as="textarea"
                                    name="paymentTermsCustom"
                                    value={currentStep?.paymentTermsCustom}
                                    onChange={changeHandler}
                                    rows={5}
                                    disabled={isPaymentDisabled}
                                    maxLength={1000}
                                    className={`w-100 mb-0 mt-3 min-height-100px ${
                                        isSubmitClicked && !currentStep?.paymentTermsCustom ? 'border-danger' : ''
                                    }`}
                                    placeholder={t('brand.campaign.briefing.step8.payment_description_field')}
                                />
                            </div>
                        )}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex flex-column">
                            <h6 className="font-weight-bold mb-0 ">{t('brand.campaign.briefing.step8.exclusivity')}</h6>
                            <span className="text-muted">
                                {t('brand.campaign.briefing.step8.exclusivity_description')}
                            </span>
                        </div>
                        <ToggleSwitch
                            small={true}
                            disabled={isDisabled}
                            id="exclusivity-approval"
                            onChange={(checked: boolean) => changeSwitchHandler(checked, 'hasExclusivity')}
                            checked={currentStep?.hasExclusivity}
                        />
                    </div>
                    <div className={`border-bottom pb-${currentStep?.hasExclusivity ? '3' : '0'}`}>
                        {currentStep?.hasExclusivity && (
                            <>
                                <Form.Control
                                    as="textarea"
                                    name="exclusivityDescription"
                                    value={currentStep?.exclusivityDescription}
                                    onChange={changeHandler}
                                    rows={5}
                                    maxLength={2000}
                                    className={`w-100 mb-0 min-height-100px ${
                                        isSubmitClicked && !currentStep?.exclusivityDescription ? 'border-danger' : ''
                                    }`}
                                    placeholder="Exclusivity - text box 2000 characters"
                                    disabled={isDisabled}
                                />

                                <div className="mt-4">
                                    <h6 className="font-weight-bold mb-0">
                                        {t('brand.campaign.briefing.step8.exclusivity_competitors')}
                                    </h6>
                                    {currentStep.exclusivityCompetitors && (
                                        <div className="row">
                                            {[...Array(exclusivityCompetitors?.length)].map((item, index) => {
                                                return (
                                                    <div className="col-md-4 mt-3 position-relative" key={index}>
                                                        <TextField
                                                            label="Competitor"
                                                            variant="outlined"
                                                            value={exclusivityCompetitors[index]?.description}
                                                            onChange={e => changeExclusivityHandler(e, index)}
                                                            name="description"
                                                            size="small"
                                                            className="w-100"
                                                            disabled={isDisabled}
                                                        />
                                                        {isDisabled ? (
                                                            ''
                                                        ) : (
                                                            <div
                                                                role="button"
                                                                onClick={() => !isDisabled && removeCompetitors(index)}
                                                                className="removeCompetitors"
                                                            >
                                                                <RiCloseCircleFill />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <Button
                                        disabled={isDisabled}
                                        onClick={addCompetitors}
                                        size="sm"
                                        variant="primary"
                                        className="my-3"
                                    >
                                        + {t('brand.campaign.briefing.step8.add')}
                                    </Button>
                                    <div className="col-md-4 pe-md-3">
                                        <h6 className="font-weight-bold mb-0 mb-3 ">
                                            {t('brand.campaign.briefing.step8.exclusivity_duration')}
                                        </h6>
                                        <CustomAutocomplete
                                            dataArray={OfferItemDurationEnum}
                                            isMultiple={false}
                                            defaultArray={currentStep?.exclusivityDuration}
                                            isWithImage={false}
                                            inputLabel="Publication Period"
                                            hasError={isSubmitClicked && !currentStep?.exclusivityDuration}
                                            queryFilterKey="exclusivityDuration"
                                            inputLabelTranslation={`Publication Period`}
                                            handleChangeParams={multipleSelectHandler}
                                            isDisabled={isDisabled}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
                        <div className="d-flex flex-column">
                            <h6 className="font-weight-bold mb-0 ">{t('brand.campaign.briefing.step8.bylaws')}</h6>
                            <span className="text-muted">{t('brand.campaign.briefing.step8.bylaws_description')}</span>
                        </div>
                        <ToggleSwitch
                            small={true}
                            id="isBylaws-approval"
                            onChange={(checked: boolean) => changeSwitchHandler(checked, 'hasByLaws')}
                            checked={currentStep?.hasByLaws}
                            disabled={isDisabled}
                        />
                    </div>
                    <div className={`border-bottom pb-${currentStep?.hasByLaws ? '3' : '0'}`}>
                        {currentStep?.hasByLaws && (
                            <div className="d-flex flex-column flex-md-row align-items-start justify-content-between">
                                <UploadImage
                                    classes="p-0"
                                    saveFile={setFiles}
                                    deleteSelectedImage={deleteSelectedImage}
                                    acceptedFormats="application/msword, image/jpeg, image/png, image/jpg, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf"
                                    previewImage={currentStep?.bylaws}
                                    isMultiple={true}
                                    disabled={isDisabled}
                                    isDeleteHidden={isDisabled}
                                />
                                <TextEditor
                                    state={currentStep?.byLawsExplanation ?? ''}
                                    classes="w-100 mb-0 ml-md-3 bylaws-editor"
                                    hasError={isSubmitClicked && !clearHtmlFromString(currentStep?.byLawsExplanation ?? '')?.length}
                                    placeholder="Explanation"
                                    disabled={isDisabled}
                                    changeTextFieldHandler={value =>
                                        setCurrentStep((prev: any) => ({
                                            ...prev,
                                            byLawsExplanation: DOMPurify.sanitize(value),
                                        }))
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div className="mt-4">
                        <StepperRouting
                            submitStepHandler={submitHandler}
                            isNextDisabled={isNextClicked}
                            status={currentStep?.status}
                        />
                    </div>
                </CardLayout>
            </Col>
            <Col md={4} className="pr-md-4">
                <CampaignOverviewCard />
            </Col>
        </Row>
    );
};
